import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Chip,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Tab,
  Tabs,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Save,
  Cancel,
  AttachMoney,
  Percent,
  Person,
  Settings,
  TrendingDown
} from '@mui/icons-material';
import feeManager from '../utils/feeManager';
import CustomerTiersDialog from './CustomerTiersDialog';
import { db } from '../firebase';
import { collection, getDocs, query } from 'firebase/firestore';

const FeeManagementPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [defaultFees, setDefaultFees] = useState(null);
  const [customerOverrides, setCustomerOverrides] = useState({});
  const [editingDefault, setEditingDefault] = useState(false);
  const [editingTiers, setEditingTiers] = useState([]);
  const [tiersDialog, setTiersDialog] = useState(false);
  const [selectedTiersCustomer, setSelectedTiersCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [testAmount, setTestAmount] = useState(100);

  useEffect(() => {
    // Initialize fee manager and subscribe to changes
    const unsubscribe = feeManager.initialize().then(() => {
      return feeManager.subscribe((data) => {
        setDefaultFees(data.defaultFees);
        setCustomerOverrides(data.customerOverrides);
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);


  // Calculate fee for testing
  const calculateTestFee = (email = null) => {
    if (!testAmount || testAmount <= 0) return null;
    return feeManager.calculateFee(parseFloat(testAmount), email);
  };

  // Handle default fees editing
  const handleEditDefault = () => {
    setEditingTiers([...defaultFees.tiers]);
    setEditingDefault(true);
  };

  const handleSaveDefault = async () => {
    setLoading(true);
    try {
      await feeManager.updateDefaultFees(
        { ...defaultFees, tiers: editingTiers },
        'admin' // Should use actual admin email from auth
      );
      setSuccess('Default fees updated successfully');
      setEditingDefault(false);
    } catch (err) {
      setError('Failed to update default fees');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDefault = () => {
    setEditingDefault(false);
    setEditingTiers([]);
  };

  // Handle tier changes
  const handleTierChange = (index, field, value) => {
    const newTiers = [...editingTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setEditingTiers(newTiers);
  };

  const handleAddTier = () => {
    setEditingTiers([
      ...editingTiers,
      { maxAmount: null, fee: 3, type: 'percentage' }
    ]);
  };

  const handleRemoveTier = (index) => {
    setEditingTiers(editingTiers.filter((_, i) => i !== index));
  };

  // Handle customer overrides

  const handleDeleteCustomer = async (email) => {
    if (!window.confirm(`Remove fee override for ${email}?`)) return;

    setLoading(true);
    try {
      await feeManager.removeCustomerOverride(email);
      setSuccess('Customer override removed successfully');
    } catch (err) {
      setError('Failed to remove customer override');
    } finally {
      setLoading(false);
    }
  };

  // Handler for custom tiers
  const handleAddCustomTiers = () => {
    setSelectedTiersCustomer(null);
    setTiersDialog(true);
  };

  const handleEditCustomTiers = (email, override) => {
    setSelectedTiersCustomer({ email, ...override });
    setTiersDialog(true);
  };

  const handleSaveCustomTiers = async (tiersData) => {
    setLoading(true);
    try {
      const { email, ...overrideData } = tiersData;
      await feeManager.setCustomerOverride(
        email,
        overrideData,
        'admin' // Should use actual admin email
      );
      setSuccess(`Custom fee tiers ${selectedTiersCustomer ? 'updated' : 'added'} successfully`);
      setTiersDialog(false);
    } catch (err) {
      setError('Failed to save custom tiers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachMoney sx={{ fontSize: 32 }} />
        Fee Management
      </Typography>

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Fee Calculator Card */}
      <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Fee Calculator
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Test Amount"
                type="number"
                value={testAmount}
                onChange={(e) => setTestAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              {calculateTestFee() && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Fee: $${calculateTestFee().feeAmount}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${calculateTestFee().feePercentage}%`}
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip
                    label={`Total: $${calculateTestFee().totalAmount}`}
                    color="success"
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Default Fees" icon={<Settings />} iconPosition="start" />
        <Tab label="Customer Overrides" icon={<Person />} iconPosition="start" />
      </Tabs>

      {/* Default Fees Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Default Fee Structure</Typography>
            {!editingDefault ? (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEditDefault}
              >
                Edit
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Save />}
                  onClick={handleSaveDefault}
                  disabled={loading}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancelDefault}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Amount Range</TableCell>
                  <TableCell>Fee Type</TableCell>
                  <TableCell>Fee Value</TableCell>
                  {editingDefault && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(editingDefault ? editingTiers : defaultFees?.tiers || []).map((tier, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {editingDefault ? (
                        <TextField
                          type="number"
                          value={tier.maxAmount || ''}
                          onChange={(e) => handleTierChange(index, 'maxAmount', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="Unlimited"
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">Up to $</InputAdornment>,
                          }}
                        />
                      ) : (
                        `${index === 0 ? 'Up to' : 'Up to'} ${tier.maxAmount ? `$${tier.maxAmount}` : 'Unlimited'}`
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDefault ? (
                        <Select
                          value={tier.type}
                          onChange={(e) => handleTierChange(index, 'type', e.target.value)}
                          size="small"
                        >
                          <MenuItem value="fixed">Fixed</MenuItem>
                          <MenuItem value="percentage">Percentage</MenuItem>
                        </Select>
                      ) : (
                        <Chip
                          label={tier.type}
                          size="small"
                          icon={tier.type === 'fixed' ? <AttachMoney /> : <Percent />}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDefault ? (
                        <TextField
                          type="number"
                          value={tier.fee}
                          onChange={(e) => handleTierChange(index, 'fee', parseFloat(e.target.value))}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">
                              {tier.type === 'fixed' ? '$' : '%'}
                            </InputAdornment>,
                          }}
                        />
                      ) : (
                        `${tier.type === 'fixed' ? '$' : ''}${tier.fee}${tier.type === 'percentage' ? '%' : ''}`
                      )}
                    </TableCell>
                    {editingDefault && (
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveTier(index)}
                          disabled={editingTiers.length === 1}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {editingDefault && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddTier}
              sx={{ mt: 2 }}
            >
              Add Tier
            </Button>
          )}

          {defaultFees?.lastUpdated && (
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              Last updated: {new Date(defaultFees.lastUpdated.toDate ? defaultFees.lastUpdated.toDate() : defaultFees.lastUpdated).toLocaleString()}
              {defaultFees.updatedBy && ` by ${defaultFees.updatedBy}`}
            </Typography>
          )}
        </Paper>
      )}

      {/* Customer Overrides Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Customer Fee Overrides</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCustomTiers}
            >
              Add Customer Fee Tiers
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer Email</TableCell>
                  <TableCell>Override Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Test Fee</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(customerOverrides).map(([email, override]) => {
                  const testFee = calculateTestFee(email);
                  return (
                    <TableRow key={email}>
                      <TableCell>{email}</TableCell>
                      <TableCell>
                        <Chip
                          label={override.type.replace(/_/g, ' ')}
                          size="small"
                          color={override.type === 'discount' ? 'success' : override.type === 'custom_tiers' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        {override.type === 'discount'
                          ? `${override.discount}% off`
                          : override.type === 'custom_tiers'
                          ? `${override.tiers?.length || 0} tiers`
                          : `${override.fee}${override.type.includes('percentage') ? '%' : '$'}`
                        }
                      </TableCell>
                      <TableCell>
                        {testFee && (
                          <Box>
                            <Typography variant="body2">
                              ${testFee.feeAmount} ({testFee.feePercentage}%)
                            </Typography>
                            {override.type === 'discount' && (
                              <Chip
                                label={`-${override.discount}%`}
                                size="small"
                                color="success"
                                icon={<TrendingDown />}
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{override.note || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditCustomTiers(email, override)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCustomer(email)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {Object.keys(customerOverrides).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No customer overrides configured
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Customer Override Dialog */}

      {/* Customer Tiers Dialog */}
      <CustomerTiersDialog
        open={tiersDialog}
        onClose={() => setTiersDialog(false)}
        onSave={handleSaveCustomTiers}
        initialData={selectedTiersCustomer}
      />
    </Box>
  );
};

export default FeeManagementPanel;
